import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Users, 
  Calendar, 
  Download,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Edit,
  Eye,
  Building2,
  X,
  Clock,
  FileText,
  TrendingUp,
  Hospital,
  Activity,
  UserCheck,
  Plane
} from 'lucide-react';
import { staffAPI } from '../../services/api';
import api from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import Modal from '../UI/Modal';
import toast from '../../utils/toast';
import { FilterButton } from '../UI/FilterButton';
import { getClassificationSolidColor } from '../../utils/classificationConfig';
import { LEAVE_TYPE_IDS } from '../../constants/leaveTypes';
import { formatLeaveDays } from '../../utils/numberUtils';
import { formatDate, formatDateShort, formatDaySpan, formatExpiryUrgencyLabel, formatServicePeriod, getCalendarDaysUntil } from '../../utils/dateUtils';
import LeavePeriodDisplay from './LeavePeriodDisplay';
import { TradeBadge } from '../UI/TradeBadge';
import { isTradeRedundantWithDepartment } from '../../config/tradeConfig';
import { StatusBadge } from '../UI/StatusBadge';
import { ClassificationBadge } from '../UI/ClassificationBadge';
import { CountBadge } from '../UI/CountBadge';
import { Pagination } from '../UI/Pagination';
import { LeaveTypeBadge } from '../UI/LeaveTypeBadge';
import { InfoBadge } from '../UI/InfoBadge';
import {
  getDisplayLeaveTypeName,
  getOilCreditedDays,
  hasCrossTypeConsumption,
  formatOilCreditDaysLabel,
  getOilCreditDaysClassName,
} from '../../utils/leaveDisplayUtils';
import { DurationWithConsumption } from './ConsumptionBreakdown';
import PillSelect from '../UI/PillSelect';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';

// Maps leave type name to a text-color class matching the DS canonical color table.
const getLeaveTypeTextColor = (leaveTypeName) => {
  if (!leaveTypeName) return 'text-gray-600';
  const n = leaveTypeName.toLowerCase();
  if (n.includes('shared parental'))                       return 'text-fuchsia-600';
  if (n.includes('paternity'))                             return 'text-purple-600';
  if (n.includes('maternity'))                             return 'text-pink-600';
  if (n.includes('extended childcare'))                    return 'text-emerald-600';
  if (n.includes('childcare'))                             return 'text-green-600';
  if (n.includes('adoption'))                              return 'text-fuchsia-600';
  if (n.includes('annual'))                                return 'text-blue-600';
  if (n.includes('urgent'))                                return 'text-orange-600';
  if (n.includes('home leave') || n.includes('home l'))   return 'text-sky-600';
  if (n.includes('hospitalisation'))                       return 'text-red-600';
  if (n.includes('outpatient') || n.includes('sick'))     return 'text-amber-600';
  if (n.includes('marriage'))                              return 'text-rose-600';
  if (n.includes('compassionate'))                         return 'text-gray-600';
  if (n.includes('unpaid'))                                return 'text-slate-600';
  if (n.includes('in lieu') || n.includes('lieu'))        return 'text-teal-600';
  if (n.includes('external activity'))                     return 'text-cyan-600';
  if (n.includes('work from home'))                        return 'text-indigo-600';
  if (n.includes('on course') || n.includes('course'))    return 'text-violet-600';
  if (n.includes('national service'))                      return 'text-lime-600';
  return 'text-gray-600';
};

/** Normalised fields for Leave History modal rows (desktop table + mobile cards). */
const getHistoryApplicationFields = (app) => {
  const leaveStatus = app.leave_status || app.status || 'unknown';
  const displayTypeName = getDisplayLeaveTypeName(app) || '—';
  const oilCredited = getOilCreditedDays(app);
  const totalDays = oilCredited != null
    ? oilCredited
    : (app.leave_total_days || app.total_days || '—');
  const appliedAt = app.leave_applied_at || app.applied_at || app.created_at;
  const daysLabel = oilCredited != null
    ? formatOilCreditDaysLabel(oilCredited, leaveStatus)
    : (typeof totalDays === 'number' || !isNaN(totalDays)
      ? formatLeaveDays(totalDays)
      : totalDays);
  const daysClassName = oilCredited != null
    ? getOilCreditDaysClassName(leaveStatus)
    : 'text-sm font-medium text-gray-900 tabular-nums';

  return { leaveStatus, displayTypeName, oilCredited, totalDays, appliedAt, daysLabel, daysClassName };
};

const LeaveBalanceManager = ({ onRefresh }) => {
  // Core state
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const expandedEmployeesRef = useRef(new Set());

  // Interim birth notification: staff_ids with active provisional credits
  const [interimStaffIds, setInterimStaffIds] = useState(new Set());

  // Filter state
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // 'low_balance', 'expiring', 'all'
  const [employmentStatusFilter, setEmploymentStatusFilter] = useState('active');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  const activeFilterCount = (searchTerm ? 1 : 0)
    + (departmentFilter ? 1 : 0)
    + (leaveTypeFilter ? 1 : 0)
    + (statusFilter ? 1 : 0)
    + (employmentStatusFilter && employmentStatusFilter !== 'active' ? 1 : 0)
    + (selectedYear ? 1 : 0);

  const clearAllFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('');
    setLeaveTypeFilter('');
    setStatusFilter('');
    setEmploymentStatusFilter('active');
    setSelectedYear(new Date().getFullYear());
  };

  // Reference data
  const [departments, setDepartments] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  // Modal state for individual adjustments
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({
    leave_type: '',
    adjustment_days: '',
    adjustment_reason: '',
    adjustment_type: 'add'
  });

  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyEmployee, setHistoryEmployee] = useState(null);
  const [historyApplications, setHistoryApplications] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchData = useCallback(async () => {
      setLoading(true);
    try {
      // Parallel fetch of all required data (+ active interim notifications)
      const staffListParams = {
        page_size: 10000,
        exclude_classification: 'system'
      };
      if (employmentStatusFilter !== 'all') {
        staffListParams.employment_status = employmentStatusFilter || 'active';
      }

      const [balancesRes, employeesRes, departmentsRes, interimRes] = await Promise.allSettled([
        staffAPI.getLeaveBalances({ 
          year: selectedYear,
          page_size: 10000,
          employment_status: employmentStatusFilter || 'active'
        }),
        staffAPI.getStaffList(staffListParams),
        staffAPI.getDepartments(),
        api.get('/interim-birth-notification/overdue/', { params: { show_all: 'true', page_size: 500 } }),
      ]);

      // Process results with better error handling
      const balances = balancesRes.status === 'fulfilled' ? 
        (balancesRes.value.data?.results || balancesRes.value.data || []) : [];
      
      const allEmployees = employeesRes.status === 'fulfilled' ? 
        (employeesRes.value.data?.results || employeesRes.value.data || []) : [];
      
      const depts = departmentsRes.status === 'fulfilled' ? 
        (Array.isArray(departmentsRes.value.data) ? departmentsRes.value.data : []) : [];

      // Build set of staff_ids with active provisional leave (interim birth notifications)
      const interimItems = interimRes.status === 'fulfilled'
        ? (interimRes.value.data?.results || [])
        : [];
      setInterimStaffIds(new Set(interimItems.map(r => r.employee_staff_id)));

      console.log('📊 Fetched data:', {
        balances: balances.length,
        employees: allEmployees.length,
        departments: depts.length,
        apiResponseStructure: {
          balancesHasResults: !!balancesRes.value.data?.results,
          balancesCount: balancesRes.value.data?.count,
          balancesNext: balancesRes.value.data?.next,
          actualResponseLength: balancesRes.value.data?.results?.length || (Array.isArray(balancesRes.value.data) ? balancesRes.value.data.length : 0)
        }
      });
      
      // Debug: Log S0001's balances from API  
      const s0001Balances = balances.filter(b => b.staff_id === 'S0001');
      console.log(`🔍 S0001 API Response: Found ${s0001Balances.length} balances`);
      s0001Balances.forEach((balance, index) => {
        console.log(`  ${index + 1}. ${balance.leave_type?.leave_type_name} (${balance.leave_type?.id}): ${balance.entitled_days} days`);
      });

      // Group balances by employee staff_id
      const balancesByEmployee = balances.reduce((acc, balance) => {
        const staffId = balance.staff_id;
        if (!acc[staffId]) acc[staffId] = [];
        acc[staffId].push(balance);
        return acc;
      }, {});

      // Merge employee data with their balances
      const employeesWithBalances = allEmployees
        .map(employee => ({
          ...employee,
          balances: balancesByEmployee[employee.staff_id] || [],
          hasBalances: (balancesByEmployee[employee.staff_id] || []).length > 0,
          full_name: employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
        }));

      // Extract unique leave types from the data
      const uniqueLeaveTypes = [...new Set(balances.map(b => b.leave_type?.leave_type_name))].filter(Boolean);

      console.log('✅ Processed data:', {
        employeesWithBalances: employeesWithBalances.length,
        totalEmployees: allEmployees.length,
        employeesWithBalances: employeesWithBalances.filter(emp => emp.hasBalances).length,
        uniqueLeaveTypes: uniqueLeaveTypes.length
      });

      setEmployees(employeesWithBalances);
      setDepartments(Array.isArray(depts) ? depts : []);
      setLeaveTypes(Array.isArray(uniqueLeaveTypes) ? uniqueLeaveTypes.map(name => ({ name })) : []);

    } catch (error) {
      console.error('Error fetching leave balance data:', error);
      toast.error('Failed to load leave balance data');
      setEmployees([]);
      setDepartments([]);
      setLeaveTypes([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, employmentStatusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter employees based on current filters
  const filteredEmployees = employees.filter(employee => {
    // Search filter
    const matchesSearch = !searchTerm || 
      employee.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.department?.department_name?.toLowerCase().includes(searchTerm.toLowerCase());

    // Department filter
    const matchesDepartment = !departmentFilter || 
      employee.department?.department_name === departmentFilter;

    // Leave type filter
    const matchesLeaveType = !leaveTypeFilter ||
      employee.balances.some(b => b.leave_type?.leave_type_name === leaveTypeFilter);

    // Status filter
    let matchesStatus = true;
    if (statusFilter === 'low_balance') {
      matchesStatus = employee.balances.some(b => (b.remaining_days || 0) < 5);
    } else if (statusFilter === 'expiring') {
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
      matchesStatus = employee.balances.some(b => {
        if (!b.expiry_date) return false;
        return new Date(b.expiry_date) <= oneMonthFromNow;
      });
    }

    return matchesSearch && matchesDepartment && matchesLeaveType && matchesStatus;
  });

  // Calculate statistics — balance-specific, actionable metrics
  const stats = (() => {
    const totalEmployees = employees.length;
    const employeesWithBalances = employees.filter(emp => emp.hasBalances).length;

    // Annual Leave specific — the leave type HR actively manages
    const allAnnualBalances = employees.flatMap(emp =>
      emp.balances.filter(b => b.leave_type?.leave_type_name === 'Annual Leave')
    );

    // Low Annual Leave: employees whose AL remaining < 5 days
    const lowAnnualLeaveCount = employees.filter(emp =>
      emp.balances.some(b =>
        b.leave_type?.leave_type_name === 'Annual Leave' && (parseFloat(b.remaining_days) || 0) < 5
      )
    ).length;

    // Carry-forward liability: total CF days across all employees (AL)
    const carryForwardDays = allAnnualBalances.reduce(
      (sum, b) => sum + (parseFloat(b.carried_forward_days) || 0), 0
    );

    // Annual Leave utilization rate
    const totalEntitled = allAnnualBalances.reduce(
      (sum, b) => sum + (parseFloat(b.entitled_days) || 0), 0
    );
    const totalUsed = allAnnualBalances.reduce(
      (sum, b) => sum + (parseFloat(b.used_days) || 0), 0
    );
    const utilizationRate = totalEntitled > 0
      ? Math.round((totalUsed / totalEntitled) * 100)
      : 0;

    // Home Leave over-cap: workers whose 2026 HL used_days exceeds their
    // entitled_days (30 cal days/calendar year, MOM-aligned). HL.used_days
    // is the calendar-days-overseas counter (root + extensions), not the
    // unpaid-only counter — see chat 953a519e (27 May 2026).
    const homeLeaveOverCapCount = employees.filter(emp =>
      emp.balances.some(b =>
        b.leave_type?.leave_type_name === 'Home Leave' &&
        (parseFloat(b.used_days) || 0) > (parseFloat(b.entitled_days) || 0) &&
        (parseFloat(b.entitled_days) || 0) > 0
      )
    ).length;

    return {
      totalEmployees,
      employeesWithBalances,
      lowAnnualLeaveCount,
      carryForwardDays: formatLeaveDays(carryForwardDays),
      utilizationRate,
      homeLeaveOverCapCount,
    };
  })();

  // Pagination derived values
  const totalPages = Math.ceil(filteredEmployees.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedEmployees = filteredEmployees.slice(startIndex, startIndex + pageSize);

  // Reset to page 1 when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, departmentFilter, leaveTypeFilter, statusFilter, employmentStatusFilter, selectedYear]);

  const toggleEmployeeExpansion = (employeeId) => {
    console.log('🔍 Toggle expansion for employee:', employeeId);
    console.log('🔍 Current expanded employees:', Array.from(expandedEmployeesRef.current));
    
    const newExpanded = new Set(expandedEmployeesRef.current);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
      console.log('🔍 Removed employee from expanded list');
    } else {
      newExpanded.add(employeeId);
      console.log('🔍 Added employee to expanded list');
    }
    
    expandedEmployeesRef.current = newExpanded;
    setExpandedEmployees(newExpanded);
    console.log('🔍 New expanded employees:', Array.from(newExpanded));
  };

  // Helper function to detect if a name is Chinese (simple detection)
  const isChineseName = (firstName, lastName) => {
    // Check if first name has multiple parts (common in Chinese names)
    if (firstName && firstName.includes(' ')) {
      return true;
    }
    
    // Check for common Chinese surnames
    const commonChineseSurnames = [
      'Chan', 'Chen', 'Cheng', 'Cheung', 'Chin', 'Chong', 'Chow', 'Chu', 'Chua', 'Chung',
      'Fong', 'Ho', 'Huang', 'Hui', 'Kang', 'Koh', 'Kong', 'Koo', 'Kwan', 'Kwok',
      'Lai', 'Lam', 'Lee', 'Leong', 'Leung', 'Li', 'Lim', 'Lin', 'Ling', 'Liu',
      'Lo', 'Loi', 'Lok', 'Loo', 'Low', 'Lui', 'Lum', 'Lung', 'Ma', 'Mah',
      'Mak', 'Man', 'Mok', 'Mui', 'Mung', 'Ng', 'Ngan', 'Ong', 'Pang', 'Poon',
      'Pun', 'Shum', 'Sin', 'So', 'Soh', 'Sung', 'Tam', 'Tan', 'Tang', 'Tay',
      'Teng', 'Ting', 'Tong', 'Tsang', 'Tse', 'Tsoi', 'Tsui', 'Tung', 'Wan', 'Wee',
      'Wong', 'Woo', 'Wu', 'Yam', 'Yap', 'Yau', 'Yeung', 'Yip', 'Yong', 'Yu',
      'Yuen', 'Yung', 'Zhang', 'Zhao', 'Zhou', 'Zhu'
    ];
    
    if (lastName && commonChineseSurnames.includes(lastName)) {
      return true;
    }
    
    return false;
  };

  // Helper function to format display name (Chinese names: Last, First; Others: First, Last)
  const getDisplayName = (firstName, lastName) => {
    if (!firstName && !lastName) return '';
    
    if (isChineseName(firstName, lastName)) {
      // Chinese format: Last Name, First Name
      return `${firstName || ''} ${lastName || ''}`.trim();
    } else {
      // Western format: First Name, Last Name
      return `${firstName || ''} ${lastName || ''}`.trim();
    }
  };

  // Delegates to classificationConfig — the single source of truth for classification colours.
  const getAvatarColor = (classification) => getClassificationSolidColor(classification);

  // Simplified avatar logic - just first letter of first name
  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return '';
    
    // Just return the first letter of the first name (or last name if no first name)
    const nameToUse = firstName || lastName;
    return nameToUse ? nameToUse.charAt(0).toUpperCase() : '';
  };

  const getLeaveTypeDisplay = (balance, effectiveRemaining = null, isPartTime = false, totalOverride = null) => {
    if (!balance) return <span className="text-gray-400">No balance</span>;

    const entitled = parseFloat(balance.entitled_days) || 0;
    const accrued = parseFloat(balance.accrued_days) || 0;
    const carriedForward = parseFloat(balance.carried_forward_days) || 0;
    // ``totalOverride`` lets sick-leave types report against the shared 60-day
    // bank (OSL + HL) instead of their per-type sub-cap, keeping the
    // "X of Y available" denominator consistent with the expanded card.
    const totalAvailable = totalOverride !== null
      ? totalOverride
      : (isPartTime
        ? accrued + carriedForward
        : entitled + carriedForward);

    // Use the cross-adjusted effective remaining when provided (shared sick leave pool),
    // otherwise fall back to the raw remaining_days from the balance record.
    const remaining = effectiveRemaining !== null
      ? effectiveRemaining
      : (parseFloat(balance.remaining_days) || 0);

    let colorClass = 'text-green-600';
    if (totalAvailable > 0) {
      const remainingPercentage = (remaining / totalAvailable) * 100;
      if (remainingPercentage >= 80) colorClass = 'text-green-600';
      else if (remainingPercentage >= 20) colorClass = 'text-yellow-600';
      else colorClass = 'text-red-600';
    }

    return (
      <div className={`text-sm ${colorClass}`}>
        <div className="font-medium">{formatLeaveDays(remaining)} days remaining</div>
        <div className="text-xs text-gray-500">of {formatLeaveDays(totalAvailable)} available</div>
      </div>
    );
  };

  /**
   * Compute the shared sick leave pool values for one employee.
   * MOM Rule: the 60-day hospitalisation entitlement INCLUDES the 14 outpatient
   * days, so OSL (hard ≤14-day sub-cap) and HL share a combined 60-day bank.
   * HL has no sub-cap below the pool — it may consume the entire bank, eating
   * into the OSL allocation. OSL cannot spill into the hospitalisation portion.
   */
  const computeSickLeavePool = (sickLeave, hospitalisation, isPartTime = false) => {
    const oslCeiling = isPartTime
      ? (parseFloat(sickLeave?.accrued_days) || 0)
      : (parseFloat(sickLeave?.entitled_days) || 0);
    const oslUsed = parseFloat(sickLeave?.used_days) || 0;
    const hlCeiling = isPartTime
      ? (parseFloat(hospitalisation?.accrued_days) || 0)
      : (parseFloat(hospitalisation?.entitled_days) || 0);
    const hlUsed = parseFloat(hospitalisation?.used_days) || 0;

    const combinedPool = oslCeiling + hlCeiling;
    const combinedUsed = oslUsed + hlUsed;
    const combinedRemaining = Math.max(0, combinedPool - combinedUsed);

    // OSL is capped by BOTH its own 14-day sub-limit AND the shared pool.
    const oslEffective = Math.min(Math.max(0, oslCeiling - oslUsed), combinedRemaining);
    // HL has NO sub-limit below the pool — per MOM the 60-day hospitalisation
    // entitlement INCLUDES the 14 outpatient days, so HL may draw the entire
    // remaining shared pool (eating into the OSL allocation). The only ceiling
    // is the shared pool itself; the stored HL entitled_days (46) must NOT cap it.
    const hlEffective = combinedRemaining;

    return { combinedPool, combinedUsed, combinedRemaining, oslEffective, hlEffective };
  };

  const handleAdjustBalance = async () => {
    try {
      if (!selectedEmployee || !adjustmentData.leave_type || !adjustmentData.adjustment_days || !adjustmentData.adjustment_reason) {
        toast.error('Please fill in all required fields');
        return;
      }

      const balance = selectedEmployee.balances.find(b => b.leave_type?.id === adjustmentData.leave_type);
      if (!balance) {
        toast.error('Leave balance not found');
        return;
      }

      await staffAPI.adjustLeaveBalance(balance.id, adjustmentData);
      toast.success('Leave balance adjusted successfully');
      setShowAdjustModal(false);
      setAdjustmentData({
        leave_type: '',
        adjustment_days: '',
        adjustment_reason: '',
        adjustment_type: 'add'
      });
      
      if (onRefresh) {
        onRefresh();
      } else {
        fetchData();
      }
    } catch (error) {
      console.error('Error adjusting balance:', error);
      toast.error('Failed to adjust balance');
    }
  };

  const handleExport = () => {
    // Placeholder for export functionality
    toast.info('Export functionality coming soon');
  };

  // View History handler — fetches leave applications for the selected employee
  const handleViewHistory = async (employee) => {
    setHistoryEmployee(employee);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setHistoryApplications([]);
    try {
      const response = await staffAPI.getLeaveApplications({
        leave_staff_id: employee.staff_id,
        ordering: '-leave_start_date',
        page_size: 200,
      });
      const apps = response.data?.results || response.data || [];
      setHistoryApplications(Array.isArray(apps) ? apps : []);
    } catch (error) {
      console.error('Error fetching leave history:', error);
      toast.error('Failed to load leave history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Close history modal
  const closeHistoryModal = useCallback(() => {
    setShowHistoryModal(false);
    setHistoryEmployee(null);
    setHistoryApplications([]);
  }, []);

  // Full per-leave-type breakdown for one employee. Shared verbatim by the
  // desktop expanded table row and the mobile card expansion so the two
  // layouts can never drift apart.
  const renderBalanceDetails = (employee, sickPool) => (
    <div className="space-y-4">
      {/* Header following design system typography */}
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-gray-900">
          Leave Balance Details
        </h3>
        <p className="text-sm text-gray-700">
          Comprehensive breakdown for {getDisplayName(employee.first_name, employee.last_name)}
        </p>
      </div>
      
      {/* Balance Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
         {Array.isArray(employee.balances) ? employee.balances.map((balance) => {
           const isPartTime = employee.employment_type === 'part_time';
           const remainingDays = parseFloat(balance.remaining_days) || 0;
           const entitledDays = parseFloat(balance.entitled_days) || 0;
           const accruedDays = parseFloat(balance.accrued_days) || 0;
           const carriedForwardDays = parseFloat(balance.carried_forward_days) || 0;
           const totalAvailable = isPartTime
             ? accruedDays + carriedForwardDays
             : entitledDays + carriedForwardDays;
           const isUnpaidLeave = balance.leave_type?.leave_type_name === 'Unpaid Leave';
           const usedDays = parseFloat(balance.used_days) || 0;

           // Outpatient Sick Leave + Hospitalisation Leave share the MOM
           // 60-day bank, so the per-type ``remaining_days`` is misleading on
           // its own: HL legitimately goes negative once it draws past its own
           // 46-day slice (it eats into the OSL allocation), and OSL overstates
           // what the bank can still fund. Surface the pool-effective remaining
           // and track the shared 60-day bank in the headline / bar instead.
           const sickTypeName = balance.leave_type?.leave_type_name;
           const isSickPoolType =
             sickTypeName === 'Outpatient Sick Leave' ||
             sickTypeName === 'Hospitalisation Leave';
           const sickEffectiveRemaining = isSickPoolType
             ? (sickTypeName === 'Outpatient Sick Leave'
                 ? sickPool.oslEffective
                 : sickPool.hlEffective)
             : null;
           // HL used past its own 46-day entitlement = days drawn from the
           // shared outpatient allocation. Surface this so "Used 58 / Entitled
           // 46" reads as intentional rather than a data error.
           const hlDrawnFromOsl =
             sickTypeName === 'Hospitalisation Leave' && usedDays > entitledDays
               ? usedDays - entitledDays
               : 0;
           const displayRemaining = isSickPoolType
             ? sickEffectiveRemaining
             : remainingDays;
           const displayTotal = isSickPoolType
             ? sickPool.combinedPool
             : totalAvailable;

           return (
          <div key={balance.id} className="bg-white rounded-xl shadow-sm border p-4">
            {/* Card Header */}
            <div className="flex justify-between items-start mb-3">
              <div className="flex flex-col gap-1 items-start">
                <LeaveTypeBadge leaveTypeName={balance.leave_type?.leave_type_name} />
                {/* Provisional badge — shown when Paternity/SPL was credited via interim notification */}
                {(['Paternity Leave', 'Shared Parental Leave'].includes(balance.leave_type?.leave_type_name)) &&
                  interimStaffIds.has(employee.staff_id) && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                    <Hospital className="w-3 h-3" />
                    Provisional
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {isPartTime && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                    Part-time
                  </span>
                )}
                {balance.leave_type?.leave_type_is_mom_statutory && (
                  <span className="badge badge-admin-primary">
                    <span className="mr-1">🇸🇬</span>
                    MOM
                  </span>
                )}
                {balance.leave_type?.leave_type_mom_reimbursement && (
                  <span className="badge badge-success">
                    <span className="mr-1">💰</span>
                    Reimbursable
                  </span>
                )}
                {/* Home Leave "Extended" badge:
                    surfaces when the worker's
                    calendar-year HL usage went past
                    the 30 cal-day planning cap (root
                    + extensions). Almost always means
                    HR approved an extension during a
                    live trip — NORMAL business, not a
                    violation. Uses InfoBadge with
                    canonical "Growth/Activity" orange
                    tone per the DS. See
                    design-system-reference.md §Badge
                    & Status Indicators and chat
                    953a519e (May 2026). */}
                {balance.leave_type?.leave_type_name === 'Home Leave' &&
                  entitledDays > 0 && remainingDays < 0 && (
                  <InfoBadge
                    color="orange"
                    label={`Extended +${formatLeaveDays(Math.abs(remainingDays))}d`}
                    size="xs"
                  />
                )}
                {/* OIL earliest-expiry header badge */}
                {balance.leave_type?.leave_type_name === 'Off in Lieu' && balance.earliest_oil_expiry_date && (() => {
                  const earliest = new Date(balance.earliest_oil_expiry_date);
                  const today = new Date(); today.setHours(0,0,0,0);
                  const d = Math.ceil((earliest - today) / 86400000);
                  if (d < 0) return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200">
                      <AlertTriangle className="w-3 h-3" /> Expired OIL
                    </span>
                  );
                  if (d <= 14) return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200">
                      <AlertTriangle className="w-3 h-3" /> Expiry {d}d
                    </span>
                  );
                  if (d <= 30) return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                      <Clock className="w-3 h-3" /> Expiry {d}d
                    </span>
                  );
                  return null;
                })()}
                {/* Event-based leave expiry badge (Paternity, Maternity, etc.) */}
                {balance.expiry_date && balance.leave_type?.leave_type_name !== 'Off in Lieu' && (() => {
                  const expiry = new Date(balance.expiry_date);
                  const today = new Date(); today.setHours(0,0,0,0);
                  const d = Math.ceil((expiry - today) / 86400000);
                  if (d < 0) return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200">
                      <AlertTriangle className="w-3 h-3" /> Expired
                    </span>
                  );
                  if (d <= 30) return (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      d <= 14
                        ? 'bg-red-100 text-red-700 border border-red-200'
                        : 'bg-amber-100 text-amber-700 border border-amber-200'
                    }`}>
                      <Clock className="w-3 h-3" /> {d}d to expiry
                    </span>
                  );
                  return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                      <Clock className="w-3 h-3" /> {d}d left
                    </span>
                  );
                })()}
              </div>
            </div>
            {/* End card header — closes the "flex justify-between items-start" div */}
            
            {/* Main Balance Info */}
            <div className="space-y-3">
              {isUnpaidLeave ? (
                /* Unpaid Leave: show positive "days taken" (no balance concept) */
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Days Taken:</span>
                    <span className="text-base font-semibold text-slate-600">
                      {formatLeaveDays(usedDays)} days
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 text-center">
                    No cap — tracked for payroll deduction
                  </div>
                </div>
              ) : (
              <>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Remaining:</span>
                  {/* Color tone for Remaining:
                      * positive → green (normal)
                      * zero → gray (exhausted)
                      * negative for HL → orange
                        (extension granted; normal —
                        matches the InfoBadge tone &
                        the DS "Growth/Activity"
                        orange used on the stats card)
                      * negative for any other type →
                        red (genuine over-allocation;
                        a real problem to investigate)
                      See chat 953a519e (May 2026). */}
                  <span className={`text-base font-semibold ${
                    displayRemaining < 0
                      ? (balance.leave_type?.leave_type_name === 'Home Leave'
                          ? 'text-orange-600'
                          : 'text-red-600')
                      : displayRemaining === 0
                        ? 'text-gray-600'
                        : 'text-green-600'
                  }`}>
                    {formatLeaveDays(displayRemaining)} days
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    {isSickPoolType ? 'Shared bank:' : 'Total Available:'}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatLeaveDays(displayTotal)} days
                  </span>
                </div>
              </div>
              
              {/* Progress Bar following design system */}
              <div className="space-y-1.5">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-200 ${
                      (() => {
                        const percentage = displayTotal > 0 ? (displayRemaining / displayTotal) * 100 : 0;
                        if (percentage >= 80) return 'bg-green-500';
                        if (percentage >= 20) return 'bg-yellow-500';
                        return 'bg-red-500';
                      })()
                    }`}
                    style={{ 
                      width: `${Math.max(displayTotal > 0 ? (displayRemaining / displayTotal) * 100 : 0, 3)}%` 
                    }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 text-center">
                  {displayTotal > 0 ? `${((displayRemaining / displayTotal) * 100).toFixed(0)}% remaining` : 'No allocation'}
                </div>
              </div>
              </>
              )}
              
              {/* Detailed Breakdown */}
              <div className="space-y-1.5 pt-2 border-t border-gray-100">
                {isPartTime && !isUnpaidLeave && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Accrued:</span>
                    <span className={`font-medium ${getLeaveTypeTextColor(balance.leave_type?.leave_type_name)}`}>{formatLeaveDays(accruedDays)}</span>
                  </div>
                )}

                {/* Carry-forward origin for event-based leaves */}
                {balance.carry_forward_origin && entitledDays === 0 && carriedForwardDays > 0 ? (
                  <>
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                      From {balance.carry_forward_origin.source_year}
                    </div>
                    {balance.carry_forward_origin.original_entitled_days != null && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Entitlement:</span>
                        <span className="font-medium text-gray-900">{formatLeaveDays(balance.carry_forward_origin.original_entitled_days)}</span>
                      </div>
                    )}
                    {balance.carry_forward_origin.used_in_source_year != null && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Used ({balance.carry_forward_origin.source_year}):</span>
                        <span className="font-medium text-red-600">{formatLeaveDays(balance.carry_forward_origin.used_in_source_year)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Carried Forward:</span>
                      <span className="font-medium text-blue-600">{formatLeaveDays(carriedForwardDays)}</span>
                    </div>
                    {usedDays > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Used ({balance.year}):</span>
                        <span className="font-medium text-red-600">{formatLeaveDays(usedDays)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {!isUnpaidLeave && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">{isPartTime ? 'FT Cap:' : 'Entitled:'}</span>
                        <span className={`font-medium ${isPartTime ? 'text-gray-500' : 'text-gray-900'}`}>{formatLeaveDays(entitledDays)}</span>
                      </div>
                    )}
                    {carriedForwardDays > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Carried Forward:</span>
                        <span className="font-medium text-blue-600">{formatLeaveDays(carriedForwardDays)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">{isUnpaidLeave ? 'Days Taken:' : 'Used:'}</span>
                      <span className={`font-medium ${isUnpaidLeave ? 'text-slate-600' : 'text-red-600'}`}>{formatLeaveDays(usedDays)}</span>
                    </div>
                  </>
                )}

                {!isPartTime && balance.accrued_days > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Accrued:</span>
                    <span className={`font-medium ${getLeaveTypeTextColor(balance.leave_type?.leave_type_name)}`}>{formatLeaveDays(balance.accrued_days || 0)}</span>
                  </div>
                )}
              </div>

              {/* Medical leave pool info for OSL/HL cards */}
              {isSickPoolType && sickPool.combinedPool > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-gray-100">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Medical leave pool:</span>
                    <span className="font-medium text-gray-700">
                      {formatLeaveDays(sickPool.combinedRemaining)} of {formatLeaveDays(sickPool.combinedPool)} remaining
                    </span>
                  </div>
                  {hlDrawnFromOsl > 0 && (
                    <p className="text-[11px] text-gray-500 leading-snug">
                      {formatLeaveDays(hlDrawnFromOsl)} day(s) of hospitalisation leave drawn from the
                      shared outpatient allocation (MOM: the 60-day hospitalisation
                      entitlement includes the 14 outpatient days).
                    </p>
                  )}
                </div>
              )}

              {/* Event-based leave expiry info */}
              {balance.expiry_date && balance.leave_type?.leave_type_name !== 'Off in Lieu' && (
                <div className="space-y-1.5 pt-2 border-t border-gray-100">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Expires:</span>
                    <span className={`font-medium ${balance.is_expired ? 'text-red-600' : 'text-gray-900'}`}>
                      {new Date(balance.expiry_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Service Information */}
              {balance.service_months !== undefined && (
                <div className="space-y-1.5 pt-2 border-t border-gray-100">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Service Period:</span>
                    <span className="font-medium text-gray-900">{formatServicePeriod(balance.service_months)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Eligibility:</span>
                    <span className={`font-medium ${balance.is_eligible_for_leave ? 'text-green-600' : 'text-red-600'}`}>
                      {balance.is_eligible_for_leave ? 'Eligible' : 'Not Eligible'}
                    </span>
                  </div>
                </div>
              )}

              {/* OIL Expiry Schedule — only for Off in Lieu balances */}
              {balance.leave_type?.leave_type_name === 'Off in Lieu' && balance.oil_expiry_entries?.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Expiry Schedule</span>
                    {balance.earliest_oil_expiry_date && (() => {
                      const daysUntil = getCalendarDaysUntil(balance.earliest_oil_expiry_date);
                      if (daysUntil == null || daysUntil > 14 || daysUntil < 0) return null;
                      return (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-600">
                          <AlertTriangle className="w-3 h-3" />
                          {daysUntil === 0 ? 'Expires today' : `${formatDaySpan(daysUntil, 'compact')} to earliest expiry`}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-[11px] text-teal-600">
                    OIL is auto-consumed (FIFO, oldest first) when Annual Leave is applied
                  </p>
                  <div className="space-y-1.5 mt-1">
                    {balance.oil_expiry_entries.map((entry, idx) => {
                      const formattedExpiry = formatDate(entry.expiry_date);
                      const workDate = entry.work_date ? formatDateShort(entry.work_date) : '—';
                      const daysUntil = entry.days_until_expiry;
                      const isExpired = entry.is_expired;
                      const isSoon = entry.is_expiring_soon;

                      let badgeCls = 'bg-green-50 text-green-700 border-green-200';
                      let badgeText = formatExpiryUrgencyLabel(daysUntil, 'compact') || `${formatDaySpan(daysUntil, 'compact')} left`;
                      if (isExpired) {
                        badgeCls = 'bg-red-50 text-red-700 border-red-200';
                        badgeText = 'Expired';
                      } else if (daysUntil === 0) {
                        badgeCls = 'bg-red-50 text-red-700 border-red-200';
                        badgeText = 'Today';
                      } else if (daysUntil <= 14) {
                        badgeCls = 'bg-red-50 text-red-700 border-red-200';
                      } else if (daysUntil <= 30) {
                        badgeCls = 'bg-amber-50 text-amber-700 border-amber-200';
                      }

                      return (
                        <div
                          key={`${entry.work_date}-${idx}`}
                          className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border text-xs ${
                            isExpired ? 'bg-gray-50 border-gray-200 opacity-60' :
                            isSoon ? 'bg-red-50/50 border-red-100' : 'bg-gray-50 border-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 w-14">{workDate}</span>
                            <span className="font-medium text-gray-900">
                              {entry.remaining_days === entry.days
                                ? `${formatLeaveDays(entry.remaining_days)}d`
                                : `${formatLeaveDays(entry.remaining_days)}/${formatLeaveDays(entry.days)}d`
                              }
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{formattedExpiry}</span>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${badgeCls}`}>
                              {badgeText}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* OIL with zero balance — show note */}
              {balance.leave_type?.leave_type_name === 'Off in Lieu' && (!balance.oil_expiry_entries || balance.oil_expiry_entries.length === 0) && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400 italic">No active OIL entries with expiry dates</p>
                </div>
              )}
            </div>
          </div>
           );
         }) : null}
    </div>
  </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

    return (
      <div className="space-y-6">
      {/* Statistics Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6 mb-6">
        {/* Employees Tracked */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-800" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalEmployees}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">{stats.employeesWithBalances} with balance records</p>
            </div>
          </div>
        </div>

        {/* Low Annual Leave */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.lowAnnualLeaveCount}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Low Annual Leave (&lt; 5 days)</p>
            </div>
          </div>
        </div>

        {/* Carry-Forward Liability */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.carryForwardDays}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Carry-Forward Days (AL)</p>
            </div>
          </div>
        </div>

        {/* AL Utilization Rate */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.utilizationRate}%</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">AL Utilization Rate</p>
            </div>
          </div>
        </div>

        {/* Workers whose calendar-year Home Leave went past the 30-day
           planning cap. In practice this almost always means HR approved an
           extension during an active trip (emergency back home) — NOT a
           violation. Uses the DS-canonical "Growth/Activity" orange metrics
           palette (bg-orange-100 + text-orange-600), not red/AlertCircle
           which would imply alarm. Plane icon = "worker is overseas".
           MOM-aligned, resets 1 Jan. See design-system-reference.md §Icon
           Color Schemes for Metrics and chat 953a519e (May 2026). */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Plane className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.homeLeaveOverCapCount}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Workers on Extended HL</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters Toolbar (admin) */}
      <div className="card">
        <div className="card-content">
          <div className="flex items-center justify-between gap-3">
            {/* Left: Search */}
            <div className="flex-1 max-w-xl min-w-0">
              <DebouncedSearchInput
                value={searchTerm}
                onSearch={setSearchTerm}
                placeholder="Search employees..."
              />
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <FilterButton
                theme="admin"
                isOpen={showFilters}
                activeCount={activeFilterCount}
                onClick={() => setShowFilters(s => !s)}
              />
              <button onClick={handleExport} title="Export" aria-label="Export" className="icon-button-admin">
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
          
        </div>
      </div>

      {/* Collapsible Filters Panel */}
      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200 overflow-visible">
          {/* Active filter chips inside panel */}
          {activeFilterCount > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {employmentStatusFilter && employmentStatusFilter !== 'active' && (
                <span className="pill pill-secondary">
                  Employment: {employmentStatusFilter === 'inactive' ? 'Inactive' : 'All'}
                  <button className="ml-1" title="Reset to Active" onClick={() => setEmploymentStatusFilter('active')}>×</button>
                </span>
              )}
              {searchTerm && (
                <span className="pill pill-secondary">
                  Search: {searchTerm}
                  <button className="ml-1" title="Clear search" onClick={() => setSearchTerm('')}>×</button>
                </span>
              )}
              {departmentFilter && (
                <span className="pill pill-secondary">
                  Department: {departmentFilter}
                  <button className="ml-1" title="Clear department" onClick={() => setDepartmentFilter('')}>×</button>
                </span>
              )}
              {leaveTypeFilter && (
                <span className="pill pill-secondary">
                  Type: {leaveTypeFilter}
                  <button className="ml-1" title="Clear leave type" onClick={() => setLeaveTypeFilter('')}>×</button>
                </span>
              )}
              {statusFilter && (
                <span className="pill pill-secondary">
                  Alert: {statusFilter === 'low_balance' ? 'Low Balance' : 'Expiring Soon'}
                  <button className="ml-1" title="Clear alert filter" onClick={() => setStatusFilter('')}>×</button>
                </span>
              )}
              {selectedYear && (
                <span className="pill pill-secondary">
                  Year: {selectedYear}
                  <button className="ml-1" title="Clear year" onClick={() => setSelectedYear(new Date().getFullYear())}>×</button>
                </span>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 overflow-visible">
            {/* Employment Status Filter */}
            <div className="form-group">
              <label className="form-label">Employment Status</label>
              <PillSelect
                icon={UserCheck}
                iconColor="text-red-600"
                theme="admin"
                className="w-full"
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'all', label: 'All' }
                ]}
                value={employmentStatusFilter}
                onChange={setEmploymentStatusFilter}
                placeholder="Active"
              />
            </div>

            {/* Department Filter */}
            <div className="form-group">
              <label className="form-label">Department</label>
              <PillSelect
                icon={Building2}
                iconColor="text-red-600"
                theme="admin"
                className="w-full"
                options={[
                  { value: '', label: 'All Departments' },
                  ...(Array.isArray(departments) ? departments.map(dept => ({
                    value: dept.department_name,
                    label: dept.department_name
                  })) : [])
                ]}
                value={departmentFilter}
                onChange={setDepartmentFilter}
                placeholder="All Departments"
              />
            </div>

            {/* Leave Type Filter */}
            <div className="form-group">
              <label className="form-label">Leave Type</label>
              <PillSelect
                icon={Calendar}
                iconColor="text-red-600"
                theme="admin"
                className="w-full"
                options={[
                  { value: '', label: 'All Leave Types' },
                  ...(Array.isArray(leaveTypes) ? leaveTypes.map(type => ({
                    value: type.name,
                    label: type.name
                  })) : [])
                ]}
                value={leaveTypeFilter}
                onChange={setLeaveTypeFilter}
                placeholder="All Leave Types"
              />
            </div>

            {/* Balance Alert Filter */}
            <div className="form-group">
              <label className="form-label">Balance Alert</label>
              <PillSelect
                icon={Activity}
                iconColor="text-red-600"
                theme="admin"
                className="w-full"
                options={[
                  { value: '', label: 'All Employees' },
                  { value: 'low_balance', label: 'Low Balance (< 5 days)' },
                  { value: 'expiring', label: 'Expiring Soon' }
                ]}
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Employees"
              />
            </div>

            {/* Leave Period (Year) Filter */}
            <div className="form-group">
              <label className="form-label">Year</label>
              <PillSelect
                icon={Clock}
                iconColor="text-red-600"
                theme="admin"
                className="w-full"
                options={Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - 1 + i).map(year => ({
                  value: year,
                  label: String(year)
                }))}
                value={selectedYear}
                onChange={(v) => setSelectedYear(parseInt(v))}
                placeholder="Year"
              />
            </div>
          </div>

          <div className="mt-3 flex items-center justify-end gap-2">
            <button onClick={clearAllFilters} className="px-4 py-1.5 text-sm border rounded-full hover:bg-gray-50">Clear all</button>
            <button onClick={() => setShowFilters(false)} className="px-4 py-1.5 text-sm border rounded-full hover:bg-gray-50">Close</button>
          </div>
        </div>
      )}
           
      {/* Employee Balances — mobile cards + desktop table (DS: wide tables
          MUST render stacked cards below md; a 6-column table can't fit a
          phone viewport). */}
      <div className="card">
        {/* Mobile: one card per employee */}
        {Array.isArray(paginatedEmployees) && paginatedEmployees.length > 0 && (
          <div className="md:hidden space-y-3 p-4">
            {paginatedEmployees.map((employee) => {
              const balances = employee.balances || [];
              const annualLeave = balances.find(b => b.leave_type?.leave_type_name === 'Annual Leave');
              const sickLeave = balances.find(b => b.leave_type?.id === LEAVE_TYPE_IDS.OUTPATIENT_SICK_LEAVE);
              const hospitalisation = balances.find(b => b.leave_type?.id === LEAVE_TYPE_IDS.HOSPITALISATION_LEAVE);
              const isPartTime = employee.employment_type === 'part_time';
              const sickPool = computeSickLeavePool(sickLeave, hospitalisation, isPartTime);
              const isExpanded = expandedEmployeesRef.current.has(employee.staff_id);

              return (
                <div key={employee.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
                  {/* Identity row */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-10 w-10 ${getAvatarColor(employee.employment_classification)} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <span className="text-sm font-semibold text-white">
                          {getInitials(employee.first_name, employee.last_name)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {getDisplayName(employee.first_name, employee.last_name)}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{employee.staff_id}</p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <ClassificationBadge
                        classification={employee.employment_classification || 'staff'}
                        label={employee.employment_classification ? undefined : 'Not Set'}
                      />
                    </div>
                  </div>

                  {/* Key facts — mirrors the desktop columns */}
                  <div className="mt-3 space-y-2 text-sm flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-gray-500 flex-shrink-0">Department</span>
                      <span className="text-gray-900 text-right min-w-0 inline-flex items-center justify-end gap-1.5 flex-wrap">
                        <span className="truncate">{employee.department?.department_name || employee.department || 'N/A'}</span>
                        {employee.trade_category &&
                          !isTradeRedundantWithDepartment(
                            employee.department?.department_name || employee.department,
                            null,
                            null,
                            employee.trade_category,
                          ) && (
                          <TradeBadge trade={employee.trade_category} size="xs" />
                        )}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-gray-500 flex-shrink-0">Annual Leave</span>
                      <div className="text-right">
                        {getLeaveTypeDisplay(annualLeave, null, isPartTime)}
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-gray-500 flex-shrink-0">Outpatient Sick</span>
                      <div className="text-right">
                        {getLeaveTypeDisplay(sickLeave, sickLeave ? sickPool.oslEffective : null, isPartTime, sickLeave ? sickPool.combinedPool : null)}
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-gray-500 flex-shrink-0">Hospitalisation</span>
                      <div className="text-right">
                        {getLeaveTypeDisplay(hospitalisation, hospitalisation ? sickPool.hlEffective : null, isPartTime, hospitalisation ? sickPool.combinedPool : null)}
                      </div>
                    </div>
                  </div>

                  {/* Full breakdown — same renderer as the desktop expanded row */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      {renderBalanceDetails(employee, sickPool)}
                    </div>
                  )}

                  {/* Actions — bottom-right footer per DS card-action standard */}
                  <div className="card-action-footer justify-end gap-2">
                    <button
                      onClick={() => toggleEmployeeExpansion(employee.staff_id)}
                      className="icon-button-admin"
                      title={isExpanded ? 'Hide Details' : 'View Details'}
                      aria-label={isExpanded ? 'Hide Details' : 'View Details'}
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedEmployee(employee);
                        setShowAdjustModal(true);
                      }}
                      className="icon-button-admin"
                      title="Adjust Balance"
                      aria-label="Adjust Balance"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleViewHistory(employee)}
                      className="icon-button-admin"
                      title="View History"
                      aria-label="View History"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Desktop: full table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Employee</th>
                <th className="table-header-cell">Department</th>
                <th className="table-header-cell text-right">Annual Leave</th>
                <th className="table-header-cell text-right">Outpatient Sick Leave</th>
                <th className="table-header-cell text-right">Hospitalisation</th>
                <th className="table-header-cell text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {Array.isArray(paginatedEmployees) ? paginatedEmployees.map((employee) => {
                const balances = employee.balances || [];
                // Use UUID matching for sick leave types, but handle multiple Annual Leave configurations
                const annualLeave = balances.find(b => b.leave_type?.leave_type_name === 'Annual Leave');
                const sickLeave = balances.find(b => b.leave_type?.id === LEAVE_TYPE_IDS.OUTPATIENT_SICK_LEAVE);
                const hospitalisation = balances.find(b => b.leave_type?.id === LEAVE_TYPE_IDS.HOSPITALISATION_LEAVE);

                // Shared 60-day sick leave bank (MOM): HL usage reduces OSL availability
                const isPartTime = employee.employment_type === 'part_time';
                const sickPool = computeSickLeavePool(sickLeave, hospitalisation, isPartTime);
                
                // Debug: Log what we found for this employee
                if (employee.staff_id === 'S0001') {
                  console.log('🔍 S0001 Balance Search:', {
                    totalBalances: balances.length,
                    annualLeave: annualLeave ? `${annualLeave.leave_type.leave_type_name} (${annualLeave.remaining_days} days)` : 'NOT FOUND',
                    sickLeave: sickLeave ? `${sickLeave.leave_type.leave_type_name} (${sickLeave.remaining_days} days)` : 'NOT FOUND',
                    hospitalisation: hospitalisation ? `${hospitalisation.leave_type.leave_type_name} (${hospitalisation.remaining_days} days)` : 'NOT FOUND',
                    allBalanceTypes: balances.map(b => `${b.leave_type.leave_type_name} (${b.leave_type.id})`)
                  });
                }
                const otherLeaves = balances.filter(b => 
                  ![annualLeave?.leave_type?.id, sickLeave?.leave_type?.id, hospitalisation?.leave_type?.id].includes(b.leave_type?.id)
                );

                const hasLowBalance = balances.some(b => (b.remaining_days || 0) < 5);
                const hasExpiring = balances.some(b => {
                  if (!b.expiry_date) return false;
                  const oneMonthFromNow = new Date();
                  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
                  return new Date(b.expiry_date) <= oneMonthFromNow;
                });

                return (
                <React.Fragment key={employee.id}>
                  <tr className="table-row">
                    <td className="table-cell">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className={`h-10 w-10 ${getAvatarColor(employee.employment_classification)} rounded-full flex items-center justify-center`}>
                            <span className="text-sm font-semibold text-white">
                              {getInitials(employee.first_name, employee.last_name)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">
                              {getDisplayName(employee.first_name, employee.last_name)}
                            </div>
                            <ClassificationBadge
                              classification={employee.employment_classification || 'staff'}
                              label={employee.employment_classification ? undefined : 'Not Set'}
                            />
                          </div>
                          <div className="text-sm text-gray-500">{employee.staff_id}</div>
                        </div>
                      </div>
                    </td>
                                        <td className="table-cell">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-900">
                          <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium">{employee.department?.department_name || employee.department || 'N/A'}</span>
                        </div>
                        {employee.trade_category &&
                          !isTradeRedundantWithDepartment(
                            employee.department?.department_name || employee.department,
                            null,
                            null,
                            employee.trade_category,
                          ) && (
                          <div className="mt-0.5">
                            <TradeBadge trade={employee.trade_category} size="xs" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="table-cell text-right">
                        {getLeaveTypeDisplay(annualLeave, null, isPartTime)}
                    </td>
                    <td className="table-cell text-right">
                        {getLeaveTypeDisplay(sickLeave, sickLeave ? sickPool.oslEffective : null, isPartTime, sickLeave ? sickPool.combinedPool : null)}
                    </td>
                    <td className="table-cell text-right">
                        {getLeaveTypeDisplay(hospitalisation, hospitalisation ? sickPool.hlEffective : null, isPartTime, hospitalisation ? sickPool.combinedPool : null)}
                        
                    </td>
                    <td className="table-cell text-right">
                      <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => toggleEmployeeExpansion(employee.staff_id)}
                            className="icon-button-admin"
                            title="View Details"
                          >
                            {expandedEmployeesRef.current.has(employee.staff_id) ? 
                              <ChevronDown className="w-4 h-4" /> : 
                              <ChevronRight className="w-4 h-4" />
                            }
                          </button>
                        <button
                          onClick={() => {
                            setSelectedEmployee(employee);
                            setShowAdjustModal(true);
                          }}
                          className="icon-button-admin"
                            title="Adjust Balance"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleViewHistory(employee)}
                          className="icon-button-admin"
                          title="View History"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>

                    {/* Expanded Details - Following Design System */}
                    {expandedEmployeesRef.current.has(employee.staff_id) && (
                      <tr>
                        <td colSpan="6" className="p-4 bg-gray-50">
                          {renderBalanceDetails(employee, sickPool)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              }) : null}
            </tbody>
          </table>
        </div>

        {(!Array.isArray(filteredEmployees) || filteredEmployees.length === 0) && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No employees found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalCount={filteredEmployees.length}
          pageSize={pageSize}
          theme="admin"
          noun="employees"
        />
      </div>

      {/* Adjust Balance Modal */}
      <Modal
        isOpen={showAdjustModal && !!selectedEmployee}
        onClose={() => setShowAdjustModal(false)}
        title={selectedEmployee ? `Adjust Leave Balance - ${selectedEmployee.full_name}` : ""}
        size="md"
        theme="admin"
        footer={
          <>
            <button
              onClick={() => setShowAdjustModal(false)}
              className="btn btn-admin-outline"
            >
              Cancel
            </button>
            <button
              onClick={handleAdjustBalance}
              className="btn btn-admin-primary"
            >
              Adjust Balance
            </button>
          </>
        }
      >
        {selectedEmployee && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Leave Type
              </label>
              <select
                className="select-admin w-full"
                value={adjustmentData.leave_type}
                onChange={(e) => setAdjustmentData({...adjustmentData, leave_type: e.target.value})}
              >
                <option value="">Select Leave Type</option>
                {Array.isArray(selectedEmployee.balances) ? selectedEmployee.balances.map(balance => (
                  <option key={balance.leave_type?.id} value={balance.leave_type?.id}>
                    {balance.leave_type?.leave_type_name}
                  </option>
                )) : null}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adjustment Type
              </label>
              <select
                className="select-admin w-full"
                value={adjustmentData.adjustment_type}
                onChange={(e) => setAdjustmentData({...adjustmentData, adjustment_type: e.target.value})}
              >
                <option value="add">Add Days</option>
                <option value="deduct">Deduct Days</option>
                <option value="set">Set Total Days</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Days
              </label>
              <input
                type="number"
                step="0.5"
                className="input-admin w-full"
                value={adjustmentData.adjustment_days}
                onChange={(e) => setAdjustmentData({...adjustmentData, adjustment_days: e.target.value})}
                placeholder="e.g., 2.5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason
              </label>
              <textarea
                rows={3}
                className="textarea-admin w-full"
                value={adjustmentData.adjustment_reason}
                onChange={(e) => setAdjustmentData({...adjustmentData, adjustment_reason: e.target.value})}
                placeholder="Reason for adjustment..."
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Leave History Modal */}
      {showHistoryModal && historyEmployee && (
        <Modal
          isOpen={showHistoryModal}
          onClose={closeHistoryModal}
          size="lg"
          theme="admin"
          className="max-w-5xl"
          customHeader={(
            <div className="modal-header">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="h-9 w-9 bg-gradient-to-br from-red-50 to-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 id="history-modal-title" className="modal-title">
                    Leave History
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5 truncate">
                    {getDisplayName(historyEmployee.first_name, historyEmployee.last_name)} · {historyEmployee.staff_id}
                  </p>
                </div>
              </div>
              <button
                onClick={closeHistoryModal}
                className="modal-close-admin flex-shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
          footer={(
            <button
              onClick={closeHistoryModal}
              className="btn btn-admin-outline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              Close
            </button>
          )}
        >
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : historyApplications.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-300" />
              <h4 className="mt-3 text-sm font-medium text-gray-900">No Leave Applications</h4>
              <p className="mt-1 text-sm text-gray-500">
                No leave applications found for this employee.
              </p>
            </div>
          ) : (
            <>
              {/* Summary strip — wraps on narrow modal widths */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  {historyApplications.length} application{historyApplications.length !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {historyApplications.filter((a) => {
                    const s = a.status || a.leave_status;
                    return s === 'approved' || s === 'auto_approved';
                  }).length} approved
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                  {historyApplications.filter((a) => ['pending', 'submitted'].includes(a.status || a.leave_status)).length} pending
                </span>
              </div>

              {/* Mobile: stacked cards (DS — modal tables need card fallback below md) */}
              <div className="md:hidden space-y-3">
                {historyApplications.map((app) => {
                  const {
                    leaveStatus,
                    displayTypeName,
                    oilCredited,
                    totalDays,
                    appliedAt,
                    daysLabel,
                    daysClassName,
                  } = getHistoryApplicationFields(app);

                  return (
                    <div
                      key={app.id}
                      className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <LeaveTypeBadge leaveTypeName={displayTypeName} size="xs" />
                        <StatusBadge status={leaveStatus} />
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-gray-500 flex-shrink-0">Period</span>
                          <div className="text-right min-w-0">
                            <LeavePeriodDisplay application={app} showIcon={false} size="sm" />
                          </div>
                        </div>
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-gray-500 flex-shrink-0">Days</span>
                          <DurationWithConsumption
                            showBreakdown={oilCredited == null && hasCrossTypeConsumption(app, displayTypeName)}
                            proposal={app.leave_consumption_proposal}
                            justify="end"
                            compact
                          >
                            <span className={daysClassName}>{daysLabel}</span>
                          </DurationWithConsumption>
                        </div>
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-gray-500 flex-shrink-0">Applied</span>
                          <span className="text-sm text-gray-500 text-right">{formatDate(appliedAt)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: applications table.
                  Do NOT wrap in overflow-x-auto — CSS computes overflow-y to
                  auto as well, so the wrapper swallows the wheel and the
                  modal body never receives it (last row clipped, no usable
                  scroll). Mobile already uses stacked cards; 5 columns fit. */}
              <div className="hidden md:block min-w-0">
                <table className="table w-full">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Leave Type</th>
                      <th className="table-header-cell">Period</th>
                      <th className="table-header-cell text-right">Days</th>
                      <th className="table-header-cell text-center">Status</th>
                      <th className="table-header-cell text-right">Applied</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {historyApplications.map((app) => {
                      const {
                        leaveStatus,
                        displayTypeName,
                        oilCredited,
                        totalDays,
                        appliedAt,
                        daysLabel,
                        daysClassName,
                      } = getHistoryApplicationFields(app);

                      return (
                        <tr key={app.id} className="table-row">
                          <td className="table-cell">
                            <LeaveTypeBadge leaveTypeName={displayTypeName} size="xs" />
                          </td>
                          <td className="table-cell">
                            <LeavePeriodDisplay application={app} showIcon={false} size="sm" />
                          </td>
                          <td className="table-cell text-right">
                            <DurationWithConsumption
                              showBreakdown={oilCredited == null && hasCrossTypeConsumption(app, displayTypeName)}
                              proposal={app.leave_consumption_proposal}
                              justify="end"
                              compact
                            >
                              <span className={daysClassName}>{daysLabel}</span>
                            </DurationWithConsumption>
                          </td>
                          <td className="table-cell text-center">
                            <StatusBadge status={leaveStatus} />
                          </td>
                          <td className="table-cell text-right">
                            <span className="text-sm text-gray-500">{formatDate(appliedAt)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
  };
  
export default LeaveBalanceManager; 